/**
 * Field-by-field before→after diff for an `ExpedienteActivityEntry` — reused by
 * the "Actividad" / "Versiones y restauración" drawer on `ExpedienteDetail`
 * (originally built for the retired `VersionHistoryPanel`). NEVER dumps raw
 * JSON at the user: unknown keys fall back to the raw key as a label, and a
 * fixed set of technical/computed columns is always excluded.
 */

// Friendly es-PA label per field key shown in the before→after diff. Keys not
// listed fall back to the raw key. `riesgo`/`score` are intentionally absent —
// they're computed, never diffed/restored verbatim (a restored version always
// RE-DERIVES risk server-side; see `restoreExpedienteVersionHandler` in
// packages/api).
// Exported: also reused by the drawer's "Expediente completo" tab (the full
// read-only snapshot render), not just this file's before→after diff.
export const FIELD_LABELS: Record<string, string> = {
  nombre: 'Nombre',
  email: 'Correo electrónico',
  phone: 'Teléfono',
  estado: 'Estado',
  status: 'Estado',
  pep: 'PEP',
  assignedTo: 'Asignado a',
  nextReviewAt: 'Próxima revisión',
  scheduledFor: 'Programada para',
  canal: 'Canal de vinculación',
  producto: 'Producto',
  docKind: 'Tipo de identificación',
  folio: 'Folio (Registro Público)',
  constitutionDate: 'Fecha de constitución',
  dateOfBirth: 'Fecha de nacimiento',
  entityType: 'Tipo de entidad',
  proposito: 'Propósito de la relación',
  paisesActividad: 'Países de actividad',
  fuenteFondos: 'Fuente de fondos',
  sector: 'Sector / actividad',
  kind: 'Subtipo',
  category: 'Categoría',
  docId: 'Identificación (cédula / RUC)',
  persona: 'Tipo de persona',
  ref: 'Referencia',
  riesgo: 'Riesgo',
  score: 'Puntaje de riesgo',
  direccionFisica: 'Dirección física',
  usPerson: 'US Person (FATCA)',
  paisResidenciaFiscal: 'País de residencia fiscal',
  multiplesNacionalidades: 'Múltiples nacionalidades',
  name: 'Nombre del documento',
  expiryDate: 'Fecha de vencimiento',
};

// Fields shown as a plain date (no time) rather than the raw ISO/short value.
// `deletedAt` is intentionally absent — it's an excluded bookkeeping column
// (see EXCLUDED_DIFF_FIELDS below), never rendered.
const DATE_FIELDS = new Set([
  'nextReviewAt',
  'dateOfBirth',
  'constitutionDate',
  'scheduledFor',
  'expiryDate',
]);

// Never diffed: technical/identity columns (id/tenant/timestamps), the
// computed risk fields (riesgo/score are re-derived on restore, not restored
// verbatim — showing them as a plain "before → after" would misleadingly
// promise the old score comes back), and actor-reference / soft-delete
// bookkeeping columns (deletedBy/createdBy/updatedBy/actorUserId are raw user
// UUIDs, deletedAt a bookkeeping timestamp — the entry already shows the
// actor's NAME via `actorName`, so these must never leak into the diff).
export const EXCLUDED_DIFF_FIELDS = new Set([
  'id',
  'tenantId',
  'ref',
  'createdAt',
  'updatedAt',
  'startedAt',
  'riesgo',
  'score',
  'riskInputs',
  'deletedBy',
  'createdBy',
  'updatedBy',
  'deletedAt',
  'actorUserId',
]);

/** Snake_case catalog values (e.g. `no_presencial`) → "No presencial". Leaves
 *  already-mixed-case text (names, estados) untouched. */
function humanizeSnake(value: string): string {
  if (!/^[a-z0-9_]+$/.test(value)) return value;
  const spaced = value.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Exported as `formatFieldValue` too — the drawer's "Expediente completo" tab
// renders a plain (non-diff) field value with the exact same es-PA formatting
// rules (dates, booleans, arrays, snake_case catalog values).
export function formatDiffValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (Array.isArray(value)) return value.length > 0 ? value.map(String).join(', ') : '—';
  if (DATE_FIELDS.has(key) && typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? value
      : new Intl.DateTimeFormat('es-PA', { dateStyle: 'medium' }).format(d);
  }
  if (typeof value === 'object') return '(detalle actualizado)';
  if (typeof value === 'string') return humanizeSnake(value);
  return String(value);
}

export interface FieldDiff {
  key: string;
  label: string;
  before: string;
  after: string;
}

/** Field-by-field before→after diff of the changed keys — never dumps raw JSON. */
export function diffActivityFields(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null
): FieldDiff[] {
  if (!before || !after) return [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const diffs: FieldDiff[] = [];
  for (const key of keys) {
    if (EXCLUDED_DIFF_FIELDS.has(key)) continue;
    const b = before[key];
    const a = after[key];
    if (JSON.stringify(b) === JSON.stringify(a)) continue;
    diffs.push({ key, label: FIELD_LABELS[key] ?? key, before: formatDiffValue(key, b), after: formatDiffValue(key, a) });
  }
  return diffs;
}
